import * as React from 'react';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { DayButton, DayPicker, getDefaultClassNames } from 'react-day-picker';

import { cn } from 'lib/utils';
import { Button, buttonVariants } from 'components/ui/button';

function CalendarDayButton({
    className,
    day,
    modifiers,
    ...props
}: React.ComponentProps<typeof DayButton>) {
    const defaultClassNames = getDefaultClassNames();
    const ref = React.useRef<HTMLButtonElement>(null);

    React.useEffect(() => {
        if (modifiers.focused) {
            ref.current?.focus();
        }
    }, [modifiers.focused]);

    return (
        <Button
            ref={ref}
            variant="ghost"
            size="icon"
            data-day={day.date.toLocaleDateString()}
            data-selected-single={
                modifiers.selected &&
                !modifiers.range_start &&
                !modifiers.range_end &&
                !modifiers.range_middle
            }
            data-range-start={modifiers.range_start}
            data-range-end={modifiers.range_end}
            data-range-middle={modifiers.range_middle}
            className={cn(
                'flex aspect-square h-8 w-8 min-w-8 flex-col gap-1 p-0 font-normal leading-none text-foreground data-[range-end=true]:rounded-md data-[range-end=true]:rounded-r-md data-[range-end=true]:bg-primary data-[range-end=true]:text-primary-foreground data-[range-middle=true]:rounded-none data-[range-middle=true]:bg-accent data-[range-middle=true]:text-accent-foreground data-[range-start=true]:rounded-md data-[range-start=true]:rounded-l-md data-[range-start=true]:bg-primary data-[range-start=true]:text-primary-foreground data-[selected-single=true]:bg-primary data-[selected-single=true]:text-primary-foreground [&>span]:text-xs [&>span]:opacity-70',
                defaultClassNames.day,
                className,
            )}
            {...props}
        />
    );
}

function Calendar({
    className,
    classNames,
    showOutsideDays = true,
    captionLayout = 'label',
    buttonVariant = 'ghost',
    formatters,
    components,
    ...props
}: React.ComponentProps<typeof DayPicker> & {
    buttonVariant?: React.ComponentProps<typeof Button>['variant'];
}) {
    const defaultClassNames = getDefaultClassNames();

    const currentYear = new Date().getFullYear();

    const start = new Date();
    start.setFullYear(currentYear - 150);

    const end = new Date();
    end.setFullYear(currentYear + 150);

    return (
        <DayPicker
            showOutsideDays={showOutsideDays}
            className={cn('group/calendar bg-popover p-3 text-popover-foreground', className)}
            captionLayout={captionLayout}
            startMonth={start}
            endMonth={end}
            formatters={{
                formatMonthDropdown: (date) => date.toLocaleString('default', { month: 'short' }),
                ...formatters,
            }}
            classNames={{
                root: cn('w-fit', defaultClassNames.root),
                months: cn('relative flex flex-col gap-4 md:flex-row', defaultClassNames.months),
                month: cn('flex w-full flex-col gap-4', defaultClassNames.month),
                nav: cn(
                    'absolute inset-x-0 top-0 flex w-full items-center justify-between gap-1',
                    defaultClassNames.nav,
                ),
                button_previous: cn(
                    buttonVariants({ variant: buttonVariant }),
                    'h-8 w-8 p-0 text-foreground select-none aria-disabled:opacity-50',
                    defaultClassNames.button_previous,
                ),
                button_next: cn(
                    buttonVariants({ variant: buttonVariant }),
                    'h-8 w-8 p-0 text-foreground select-none aria-disabled:opacity-50',
                    defaultClassNames.button_next,
                ),
                month_caption: cn(
                    'flex h-8 w-full items-center justify-center px-8',
                    defaultClassNames.month_caption,
                ),
                dropdowns: cn(
                    'flex h-8 w-full items-center justify-center gap-1.5 text-sm font-medium',
                    defaultClassNames.dropdowns,
                ),
                dropdown_root: cn(
                    'relative rounded-md border border-[hsl(var(--border))] shadow-sm focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/50',
                    defaultClassNames.dropdown_root,
                ),
                dropdown: cn('absolute inset-0 opacity-0', defaultClassNames.dropdown),
                caption_label: cn(
                    'select-none font-medium text-foreground',
                    captionLayout === 'label'
                        ? 'text-sm'
                        : 'flex h-8 items-center gap-1 rounded-md pl-2 pr-1 text-sm [&>svg]:h-3.5 [&>svg]:w-3.5 [&>svg]:text-muted-foreground',
                    defaultClassNames.caption_label,
                ),
                month_grid: cn(
                    'w-full border-collapse bg-popover text-popover-foreground',
                    defaultClassNames.month_grid,
                ),
                weekdays: cn('flex', defaultClassNames.weekdays),
                weekday: cn(
                    'flex-1 select-none rounded-md text-[0.8rem] font-normal text-muted-foreground',
                    defaultClassNames.weekday,
                ),
                week: cn('mt-2 flex w-full bg-transparent', defaultClassNames.week),
                week_number_header: cn('w-8 select-none', defaultClassNames.week_number_header),
                week_number: cn(
                    'select-none text-[0.8rem] text-muted-foreground',
                    defaultClassNames.week_number,
                ),
                day: cn(
                    'group/day relative aspect-square h-full w-full select-none bg-transparent p-0 text-center [&:first-child[data-selected=true]_button]:rounded-l-md [&:last-child[data-selected=true]_button]:rounded-r-md',
                    defaultClassNames.day,
                ),
                range_start: cn('rounded-l-md bg-accent', defaultClassNames.range_start),
                range_middle: cn('rounded-none', defaultClassNames.range_middle),
                range_end: cn('rounded-r-md bg-accent', defaultClassNames.range_end),
                today: cn(
                    'rounded-md bg-accent text-accent-foreground data-[selected=true]:rounded-none',
                    defaultClassNames.today,
                ),
                outside: cn(
                    'text-muted-foreground aria-selected:text-muted-foreground',
                    defaultClassNames.outside,
                ),
                disabled: cn('text-muted-foreground opacity-50', defaultClassNames.disabled),
                hidden: cn('invisible', defaultClassNames.hidden),
                ...classNames,
            }}
            components={{
                Root: ({ className: rootClassName, rootRef, ...rootProps }) => (
                    <div
                        data-slot="calendar"
                        ref={rootRef}
                        className={cn(rootClassName)}
                        {...rootProps}
                    />
                ),
                Chevron: ({ className: chevronClassName, orientation, ...chevronProps }) => {
                    if (orientation === 'left') {
                        return (
                            <ChevronLeft
                                className={cn('h-4 w-4', chevronClassName)}
                                {...chevronProps}
                            />
                        );
                    }

                    if (orientation === 'right') {
                        return (
                            <ChevronRight
                                className={cn('h-4 w-4', chevronClassName)}
                                {...chevronProps}
                            />
                        );
                    }

                    return (
                        <ChevronDown
                            className={cn('h-4 w-4', chevronClassName)}
                            {...chevronProps}
                        />
                    );
                },
                DayButton: CalendarDayButton,
                WeekNumber: ({ children, ...weekNumberProps }) => (
                    <td {...weekNumberProps}>
                        <div className="flex h-8 w-8 items-center justify-center text-center">
                            {children}
                        </div>
                    </td>
                ),
                ...components,
            }}
            {...props}
        />
    );
}

Calendar.displayName = 'Calendar';

export { Calendar, CalendarDayButton };
